<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { AlertTriangle } from 'lucide-vue-next'

interface Props {
  open?: boolean
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  loadingText?: string
  type?: 'warning' | 'danger' | 'info'
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  title: 'Confirm Action',
  message: 'Are you sure you want to continue?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  loadingText: 'Logging out...',
  type: 'warning',
  isLoading: false
})

const emit = defineEmits<{
  'update:open': [value: boolean]
  'confirm': []
  'cancel': []
}>()

const closeModal = () => {
  emit('update:open', false)
  emit('cancel')
}

const handleConfirm = () => {
  emit('confirm')
}

const getIconColor = () => {
  switch (props.type) {
    case 'danger':
      return 'text-red-600'
    case 'warning':
      return 'text-brand-blue'
    case 'info':
      return 'text-brand-blue'
    default:
      return 'text-brand-blue'
  }
}

const getIconBgColor = () => {
  switch (props.type) {
    case 'danger':
      return 'bg-red-100'
    case 'warning':
      return 'bg-brand-blue/10'
    case 'info':
      return 'bg-brand-blue/10'
    default:
      return 'bg-brand-blue/10'
  }
}

const getConfirmButtonColor = () => {
  switch (props.type) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    case 'warning':
      return 'bg-brand-blue hover:bg-brand-green focus:ring-brand-blue'
    case 'info':
      return 'bg-brand-blue hover:bg-brand-green focus:ring-brand-blue'
    default:
      return 'bg-brand-blue hover:bg-brand-green focus:ring-brand-blue'
  }
}
</script>

<template>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" @close="() => {}" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900 bg-opacity-40 pointer-events-none" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto pointer-events-none">
        <div class="flex min-h-full items-center justify-center p-4 text-center pointer-events-none">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all border border-brand-blue/10 pointer-events-auto mx-4"
            >
              <!-- Icon Container (centered layout like BaseLoadingDialog) -->
              <div class="flex flex-col items-center text-center">
                <!-- Warning Icon -->
                <div class="relative mb-6">
                  <div :class="[
                    'w-16 h-16 flex items-center justify-center rounded-2xl shadow-lg border',
                    getIconBgColor(),
                    'border-brand-blue/20'
                  ]">
                    <AlertTriangle :class="['w-8 h-8', getIconColor()]" aria-hidden="true" />
                  </div>
                  <!-- Decorative elements like BaseLoadingDialog -->
                  <div class="absolute -top-1 -right-1 w-4 h-4 bg-brand-blue/30 rounded-full animate-pulse"></div>
                  <div class="absolute -bottom-1 -left-1 w-3 h-3 bg-brand-blue/30 rounded-full animate-pulse delay-1000"></div>
                </div>
                
                <!-- Title and Message -->
                <h3 class="text-xl font-bold text-brand-blue mb-2 tracking-tight">{{ title }}</h3>
                <p class="text-gray-600 mb-6 leading-relaxed">{{ message }}</p>
                
                <!-- Action Buttons -->
                <div class="flex flex-col sm:flex-row gap-3 w-full">
                <BaseButton
                  @click="closeModal"
                  variant="outline"
                  :disabled="isLoading"
                  class="flex-1 px-6 py-3 text-sm font-semibold text-brand-blue bg-white border border-brand-blue/20 rounded-xl hover:bg-brand-blue/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-all duration-200"
                >
                  {{ cancelText }}
                </BaseButton>
                <BaseButton
                  @click="handleConfirm"
                  :disabled="isLoading"
                  :class="[
                    'flex-1 px-6 py-3 text-sm font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200',
                    getConfirmButtonColor(),
                    isLoading && 'opacity-50 cursor-not-allowed'
                  ]"
                >
                  <span v-if="isLoading" class="flex items-center justify-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ loadingText }}
                  </span>
                  <span v-else>{{ confirmText }}</span>
                </BaseButton>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
